import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentResult({ mode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "checking"); // checking | paid | pending | cancelled | error

  useEffect(() => {
    if (mode === "cancel") return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { await refreshUser(); setState("paid"); return; }
        if (tries >= 8) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (tries >= 8) setState("error"); else setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grain bg-cream flex items-center justify-center p-6 relative">
      <div className="w-full max-w-md bg-white rounded-3xl border border-line p-10 text-center relative z-10 animate-fade-up" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-teal mx-auto" size={44} />
            <h1 className="font-serif text-2xl text-charcoal mt-6">Confirming your subscription…</h1>
            <p className="text-subtle mt-2">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="text-teal mx-auto" size={56} />
            <h1 className="font-serif text-3xl text-charcoal mt-6">You're all set</h1>
            <p className="text-subtle mt-2">Thank you. Your subscription is active — your story awaits.</p>
            <button data-testid="payment-continue-btn" onClick={() => navigate("/home")} className="mt-8 bg-coral hover:bg-coraldark text-white px-8 py-3.5 rounded-full font-semibold">Continue</button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="text-sage mx-auto" size={44} />
            <h1 className="font-serif text-2xl text-charcoal mt-6">Almost there</h1>
            <p className="text-subtle mt-2">Your payment is processing. It may take a minute to confirm.</p>
            <button onClick={() => navigate("/profile")} className="mt-8 bg-teal text-cream px-8 py-3.5 rounded-full font-semibold">Back to settings</button>
          </>
        )}
        {state === "cancelled" && (
          <>
            <XCircle className="text-subtle mx-auto" size={52} />
            <h1 className="font-serif text-2xl text-charcoal mt-6">No problem</h1>
            <p className="text-subtle mt-2">Your checkout was cancelled — nothing has been charged.</p>
            <button onClick={() => navigate("/profile")} className="mt-8 bg-teal text-cream px-8 py-3.5 rounded-full font-semibold">Back to settings</button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="text-destructive mx-auto" size={52} />
            <h1 className="font-serif text-2xl text-charcoal mt-6">Something went wrong</h1>
            <p className="text-subtle mt-2">We couldn't confirm your payment. Please try again from settings.</p>
            <button onClick={() => navigate("/profile")} className="mt-8 bg-teal text-cream px-8 py-3.5 rounded-full font-semibold">Back to settings</button>
          </>
        )}
      </div>
    </div>
  );
}
